import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const KpiAnalyzerViewResult = ({result}) => {
  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'increasing':
        return <PhosphorIcons.TrendUp size={16} className="text-success" />;
      case 'decreasing':
        return <PhosphorIcons.TrendDown size={16} className="text-error" />;
      default:
        return <PhosphorIcons.Minus size={16} className="text-warning" />;
    }
  };

  const getContextClarityColor = (clarity) => {
    switch(clarity) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* KPI Metrics Section */}
        <div className="space-y-4">
          {/* Metrics Found Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartLineUp size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Key Performance Indicators</h3>
              </div>

              <div className="space-y-3">
                {result?.kpi_analysis?.metrics_found?.length > 0 ? (
                  result.kpi_analysis.metrics_found.map((metric, index) => (
                    <div key={index} className="p-3 bg-base-200 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium capitalize">{metric.name}</span>
                        <div className="flex gap-2">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                            {metric.category}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                            {Math.round(metric.confidence * 100)}% confidence
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold">{metric.value}</span>
                          <span className="text-sm text-base-content/70">{metric.unit}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(metric.context?.trend)}
                          <span className="text-sm">{metric.timeframe}</span>
                        </div>
                      </div>

                      {metric.context?.benchmark && (
                        <div className="mt-2 text-sm text-base-content/70">
                          Benchmark: {metric.context.benchmark} {metric.unit}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-3 bg-base-200 rounded-lg text-base-content/70">
                    No metrics found in the analysis
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Metric Relationships Card */}
          {result?.kpi_analysis?.relationships?.length > 0 && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <PhosphorIcons.Graph size={20} weight="bold" className="text-secondary"/>
                  </div>
                  <h3 className="card-title text-lg">Metric Relationships</h3>
                </div>

                <div className="space-y-3">
                  {result.kpi_analysis.relationships.map((relationship, index) => (
                    <div key={index} className="p-3 bg-base-200 rounded-lg">
                      <div className="flex flex-col gap-2">
                        <div className="font-medium">{relationship.primary_metric}</div>
                        <div className="flex flex-wrap gap-2">
                          {relationship.related_metrics.map((metric, idx) => (
                            <span key={idx} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                              {metric}
                            </span>
                          ))}
                        </div>
                        <div className="text-sm text-base-content/70">
                          Correlation: {relationship.correlation_type}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Analysis Quality Section */}
        <div className="space-y-4">
          {/* Extraction Quality Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Analysis Quality</h3>
              </div>

              <div className="space-y-3">
                {/* Overall Confidence */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Overall Confidence</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.metadata?.extraction_quality?.overall_confidence * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.metadata?.extraction_quality?.overall_confidence * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Completeness */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Completeness</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                      {Math.round(result?.metadata?.extraction_quality?.completeness * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-primary"
                      style={{width: `${result?.metadata?.extraction_quality?.completeness * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Context Clarity */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Context Clarity</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getContextClarityColor(result?.metadata?.extraction_quality?.context_clarity)}`}>
                      {result?.metadata?.extraction_quality?.context_clarity}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KpiAnalyzerViewResult;